import { useCallback, useEffect, useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

interface NotaRow {
  chave: string
  cnpjRemetente: string
  nomeRemetente: string
  emissao: string
  valor: string
  situacao: string
  aceite: 'aceito' | 'recusado' | null
}

interface Filtros {
  periodoDe: string
  periodoAte: string
  participante: string
  chave: string
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.82rem', height: 30, background: '#fff',
}

const label: React.CSSProperties = {
  fontSize: '0.72rem', color: '#6b7280', fontWeight: 600,
}

function campo(rotulo: string, children: React.ReactNode) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

const thBase: React.CSSProperties = {
  padding: '8px 6px', whiteSpace: 'nowrap', color: '#334155', fontWeight: 700,
}

const SIT_BG: Record<string, string> = {
  Autorizada: '#dcfce7', Denegada: '#fee2e2', Cancelada: '#f3f4f6', 'A Consultar': '#fef3c7',
}
const SIT_COLOR: Record<string, string> = {
  Autorizada: '#15803d', Denegada: '#dc2626', Cancelada: '#6b7280', 'A Consultar': '#b45309',
}

function fmtMoeda(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtData(iso: string) {
  return iso.slice(0, 10).split('-').reverse().join('/')
}

function fmtCnpj(doc: string) {
  const n = (doc || '').replace(/\D/g, '').padStart(14, '0')
  return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

function filtroPadrao(): Filtros {
  const hoje = new Date()
  const inicio = new Date(hoje)
  inicio.setDate(inicio.getDate() - 30)
  return {
    periodoDe: inicio.toISOString().slice(0, 10),
    periodoAte: hoje.toISOString().slice(0, 10),
    participante: '',
    chave: '',
  }
}

function seloAceite(aceite: NotaRow['aceite']) {
  if (aceite === 'aceito') {
    return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.74rem', fontWeight: 600, background: '#dcfce7', color: '#15803d' }}>Aceito</span>
  }
  if (aceite === 'recusado') {
    return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.74rem', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>Recusado</span>
  }
  return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.74rem', fontWeight: 600, background: '#f3f4f6', color: '#6b7280' }}>Pendente</span>
}

export default function AceiteDebitoNFe() {
  const { empresaCod, empresaAtual } = useEmpresa()
  const [filtros, setFiltros] = useState<Filtros>(filtroPadrao)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroPadrao)
  const [notas, setNotas] = useState<NotaRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [atualizando, setAtualizando] = useState(false)
  const [msgAtualizar, setMsgAtualizar] = useState<{ texto: string; ok: boolean } | null>(null)
  const [processando, setProcessando] = useState<string | null>(null)
  const [msgAcao, setMsgAcao] = useState<{ texto: string; ok: boolean } | null>(null)

  const carregar = useCallback(async () => {
    if (!empresaCod) return
    setCarregando(true)
    setErro(null)
    try {
      const params: Record<string, string> = { empCod: String(empresaCod) }
      if (pesquisaAtiva.periodoDe)    params.dataInicio   = pesquisaAtiva.periodoDe
      if (pesquisaAtiva.periodoAte)   params.dataFim      = pesquisaAtiva.periodoAte
      if (pesquisaAtiva.participante) params.participante = pesquisaAtiva.participante
      if (pesquisaAtiva.chave)        params.chave        = pesquisaAtiva.chave
      const r = await api.get('/nfe/aceite-debito', { params })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [empresaCod, pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p) }

  async function atualizarLista() {
    if (!empresaCod || atualizando) return
    setAtualizando(true)
    setMsgAtualizar(null)
    try {
      const r = await api.post('/nfe/aceite-debito/atualizar', { empCod: empresaCod })
      const res = r.data
      if (res.erro) {
        setMsgAtualizar({ texto: res.erro, ok: false })
      } else if (res.cStat === '138') {
        setMsgAtualizar({ texto: `${res.novas} nota(s) nova(s) encontrada(s).`, ok: true })
      } else if (res.cStat === '137') {
        setMsgAtualizar({ texto: 'Nenhum documento novo encontrado.', ok: true })
      } else {
        setMsgAtualizar({ texto: res.cStat ? `cStat ${res.cStat}: ${res.xMotivo}` : 'Sem resposta da SEFAZ', ok: false })
      }
      await carregar()
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao atualizar lista', ok: false })
    } finally {
      setAtualizando(false)
    }
  }

  async function darAceite(chave: string, aceitar: boolean) {
    if (processando) return
    if (!window.confirm(aceitar
      ? 'Confirma o aceite do débito referente a esta nota de crédito?'
      : 'Confirma a recusa do débito referente a esta nota de crédito?')) return

    setProcessando(chave)
    setMsgAcao(null)
    try {
      const r = await api.post('/nfe/aceite-debito/aceitar', { chave, empCod: empresaCod, aceitar })
      const res = r.data
      if (res.aceito) {
        setMsgAcao({ texto: `Evento registrado (protocolo ${res.protocolo ?? '-'}).`, ok: true })
      } else {
        setMsgAcao({ texto: res.erro || `cStat ${res.cStat}: ${res.xMotivo}`, ok: false })
      }
      await carregar()
    } catch (e: any) {
      setMsgAcao({ texto: e?.response?.data?.error || 'Erro ao enviar evento', ok: false })
    } finally {
      setProcessando(null)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.4rem' }}>
        Aceite de Débito
      </h2>
      <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
        Evento 211128 — aceite ou recuse o débito quando um cliente emitir Nota de Crédito referenciando sua empresa.
      </p>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        padding: '0.75rem 0.9rem', marginBottom: '1rem',
      }}>
        {campo('EMPRESA', (
          <div style={{ ...inp, width: 220, display: 'flex', alignItems: 'center', background: '#f3f4f6', color: '#6b7280' }}>
            {empresaAtual ? `${empresaAtual.empcod} - ${empresaAtual.empfan || empresaAtual.empnom}` : '—'}
          </div>
        ))}
        {campo('PERÍODO EMISSÃO DE', <input style={{ ...inp, width: 140 }} type="date" value={filtros.periodoDe}
          onChange={e => setFiltros(f => ({ ...f, periodoDe: e.target.value }))} />)}
        {campo('ATÉ', <input style={{ ...inp, width: 140 }} type="date" value={filtros.periodoAte}
          onChange={e => setFiltros(f => ({ ...f, periodoAte: e.target.value }))} />)}
        {campo('REMETENTE (CNPJ)', (
          <input style={{ ...inp, width: 160 }} value={filtros.participante}
            placeholder="CNPJ do cliente"
            onChange={e => setFiltros(f => ({ ...f, participante: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        {campo('CHAVE NF-E', (
          <input style={{ ...inp, width: 220 }} value={filtros.chave}
            onChange={e => setFiltros(f => ({ ...f, chave: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
            style={{ height: 30, alignSelf: 'flex-end' }}>
            {carregando ? 'Buscando...' : 'Pesquisar'}
          </button>
          <button className="btn btn-sm" onClick={limpar} style={{ height: 30, alignSelf: 'flex-end', color: '#6b7280' }}>
            Limpar
          </button>
          <button className="btn btn-sm" onClick={atualizarLista} disabled={atualizando}
            style={{ height: 30, alignSelf: 'flex-end', background: '#334155', color: '#fff' }}>
            {atualizando ? 'Consultando SEFAZ...' : 'Atualizar Lista'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {msgAtualizar && (
        <div className={`alert ${msgAtualizar.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {msgAtualizar.texto}
        </div>
      )}
      {msgAcao && (
        <div className={`alert ${msgAcao.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {msgAcao.texto}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table className="table" style={{ fontSize: '0.78rem', minWidth: 900, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#eef2f7', borderBottom: '2px solid #cbd5e1' }}>
              <th style={{ ...thBase, width: 130 }}>Remetente (CNPJ)</th>
              <th style={{ ...thBase }}>Nome</th>
              <th style={{ ...thBase, width: 80 }}>Emissão</th>
              <th style={{ ...thBase, width: 100, textAlign: 'right' }}>Valor R$</th>
              <th style={{ ...thBase, width: 100 }}>Situação NF-e</th>
              <th style={{ ...thBase, width: 100 }}>Aceite</th>
              <th style={{ ...thBase, width: 180, textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
            )}
            {!carregando && notas.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma nota encontrada</td></tr>
            )}
            {notas.map((n, idx) => (
              <tr key={n.chave} style={{ borderBottom: '1px solid #e5e7eb', background: idx % 2 === 1 ? '#f0f2f5' : '#fff' }}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.74rem', whiteSpace: 'nowrap', padding: '6px' }}>{fmtCnpj(n.cnpjRemetente)}</td>
                <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px' }} title={n.nomeRemetente}>
                  {n.nomeRemetente}
                </td>
                <td style={{ whiteSpace: 'nowrap', padding: '6px' }}>{fmtData(n.emissao)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, padding: '6px' }}>{fmtMoeda(n.valor)}</td>
                <td style={{ padding: '6px' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                    fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap',
                    background: SIT_BG[n.situacao] ?? '#f3f4f6', color: SIT_COLOR[n.situacao] ?? '#374151',
                  }}>
                    {n.situacao}
                  </span>
                </td>
                <td style={{ padding: '6px' }}>{seloAceite(n.aceite)}</td>
                <td style={{ textAlign: 'center', padding: '6px' }}>
                  {n.aceite ? (
                    <span style={{ color: '#94a3b8', fontSize: '0.76rem' }}>—</span>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      <button
                        className="btn btn-sm"
                        disabled={processando === n.chave}
                        onClick={() => darAceite(n.chave, true)}
                        style={{ background: '#15803d', color: '#fff', height: 26, padding: '0 10px', fontSize: '0.74rem' }}
                      >
                        Aceitar
                      </button>
                      <button
                        className="btn btn-sm"
                        disabled={processando === n.chave}
                        onClick={() => darAceite(n.chave, false)}
                        style={{ background: '#b91c1c', color: '#fff', height: 26, padding: '0 10px', fontSize: '0.74rem' }}
                      >
                        Recusar
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
